/*
  Free shipping bar (custom, not part of Essence).
  Keeps every <free-shipping-bar> in sync with the cart. The theme only
  announces product-page adds, so this also watches the cart endpoints that
  the theme and apps call (add, change, update, clear) and re-reads /cart.js
  after each one.
*/
(() => {
  if (window.customElements.get('free-shipping-bar')) return;

  const CART_REQUEST = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/;
  const bars = new Set();
  let refreshTimer = null;

  const cartUrl = () => `${window.Shopify?.routes?.root || '/'}cart.js`;

  function scheduleRefresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(async () => {
      try {
        const response = await fetch(cartUrl(), { headers: { Accept: 'application/json' } });
        const cart = await response.json();
        bars.forEach((bar) => bar.render(cart.total_price, cart.item_count));
      } catch (error) {
        console.error('Free shipping bar: could not read the cart', error);
      }
    }, 150);
  }

  // Watch cart requests made with fetch...
  const originalFetch = window.fetch;
  window.fetch = function (...args) {
    const request = originalFetch.apply(this, args);
    try {
      const url = typeof args[0] === 'string' ? args[0] : args[0]?.url;
      if (url && CART_REQUEST.test(url)) request.then(scheduleRefresh, () => {});
    } catch (error) {}
    return request;
  };

  // ...and with XMLHttpRequest (used by some apps).
  const originalOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    try {
      if (url && CART_REQUEST.test(String(url))) this.addEventListener('loadend', scheduleRefresh);
    } catch (error) {}
    return originalOpen.call(this, method, url, ...rest);
  };

  // Free shipping is U.S.-only. Ask Shopify where the visitor is (once per
  // visit) and hide every bar if they're outside the United States.
  const SHIP_COUNTRY = 'US';
  const GEO_KEY = 'free-shipping-bar:country';

  function applyCountry(country) {
    if (country && country !== SHIP_COUNTRY) document.documentElement.setAttribute('data-fsb-hidden', '');
  }

  let storedCountry = null;
  try {
    storedCountry = sessionStorage.getItem(GEO_KEY);
  } catch (error) {}

  if (storedCountry) {
    applyCountry(storedCountry);
  } else {
    originalFetch(`${window.Shopify?.routes?.root || '/'}browsing_context_suggestions.json?country[enabled]=true`)
      .then((response) => response.json())
      .then((data) => {
        const country = data?.detected_values?.country?.handle;
        if (!country) return;
        try {
          sessionStorage.setItem(GEO_KEY, country);
        } catch (error) {}
        applyCountry(country);
      })
      .catch(() => {});
  }

  document.addEventListener('product:added-to-cart', scheduleRefresh);
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) scheduleRefresh();
  });

  function formatMoney(cents, format) {
    const value = cents / 100;
    const withSeparators = (amount, decimals, thousands, decimal) => {
      const [whole, fraction] = amount.toFixed(decimals).split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction ? grouped + decimal + fraction : grouped;
    };
    const trimZeros = (text, decimal) => text.replace(new RegExp(`\\${decimal}00$`), '');

    return (format || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return withSeparators(value, 0, ',', '.');
        case 'amount_with_comma_separator':
          return trimZeros(withSeparators(value, 2, '.', ','), ',');
        case 'amount_no_decimals_with_comma_separator':
          return withSeparators(value, 0, '.', ',');
        default:
          return trimZeros(withSeparators(value, 2, ',', '.'), '.');
      }
    });
  }

  // Last progress shown, so a bar that the theme re-renders (the cart drawer
  // reloads its HTML after each change) animates from where it was.
  let lastProgress = null;

  class FreeShippingBar extends HTMLElement {
    connectedCallback() {
      this.message = this.querySelector('[data-fsb-message]');
      this.track = this.querySelector('[data-fsb-track]');

      // Cart drawer / cart page bars take their settings from the
      // "Free shipping bar" section (window.FreeShippingBarConfig).
      if (this.hasAttribute('data-from-config')) {
        const config = window.FreeShippingBarConfig;
        if (!config || !config.showInCart) {
          this.hidden = true;
          return;
        }

        this.dataset.threshold = config.threshold;
        this.dataset.messageProgress = config.messages.progress;
        this.dataset.messageSuccess = config.messages.success;
        this.dataset.messageEmpty = config.messages.empty || '';
        this.style.setProperty('--fsb-fill', config.fill);
        bars.add(this);
        this.hidden = false;

        // Start from the last progress shown (so a re-rendered drawer animates
        // from where it was), force a layout, then render the real values.
        if (lastProgress !== null) {
          this.style.setProperty('--fsb-progress', lastProgress);
          void this.offsetWidth;
        }
        this.render(Number(this.dataset.total) || 0, Number(this.dataset.count) || 0);
        return;
      }

      bars.add(this);
      const progress = this.style.getPropertyValue('--fsb-progress');
      if (lastProgress !== null && String(lastProgress) !== progress.trim()) {
        this.style.setProperty('--fsb-progress', lastProgress);
        void this.offsetWidth;
        this.style.setProperty('--fsb-progress', progress);
      }
      lastProgress = progress.trim();
    }

    disconnectedCallback() {
      bars.delete(this);
    }

    render(totalCents, itemCount) {
      if (this.hasAttribute('data-hide-empty')) this.hidden = itemCount === 0;

      const rate = Number(window.Shopify?.currency?.rate) || 1;
      const threshold = Math.round(Number(this.dataset.threshold) * rate);
      const progress = threshold > 0 ? Math.min(totalCents / threshold, 1) : 1;
      const format = this.dataset.moneyFormat;

      let template = this.dataset.messageProgress;
      if (totalCents >= threshold) {
        template = this.dataset.messageSuccess;
      } else if (totalCents === 0 && this.dataset.messageEmpty) {
        template = this.dataset.messageEmpty;
      }

      this.message.textContent = template
        .replaceAll('[amount]', formatMoney(Math.max(threshold - totalCents, 0), format))
        .replaceAll('[threshold]', formatMoney(threshold, format));

      this.style.setProperty('--fsb-progress', progress);
      lastProgress = String(progress);
      this.track.setAttribute('aria-valuemax', threshold);
      this.track.setAttribute('aria-valuenow', Math.min(totalCents, threshold));
    }
  }

  window.customElements.define('free-shipping-bar', FreeShippingBar);
})();
